<script setup lang="ts">
import { computed } from 'vue';
import { User, HelpCircle } from 'lucide-vue-next';

// 胶水集成：使用 shadcn-vue 组件
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * 魔盒UID输入组件
 * @description 用于收集用户的JX3BOX魔盒UID（可选）
 */

interface Props {
  modelValue: string;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const uid = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value.trim()),
});

// UID 验证（数字，可选）
const isValidUid = computed(() => {
  if (!uid.value) return true; // 可选字段，空值视为有效
  return /^\d+$/.test(uid.value);
});
</script>

<template>
  <div class="space-y-4">
    <!-- 标题栏 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Label class="text-sm font-medium">魔盒UID</Label>
        <Badge variant="secondary" class="text-xs">可选</Badge>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger as-child>
              <HelpCircle class="w-4 h-4 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent class="max-w-xs">
              <p class="text-sm">
                您的 JX3BOX 魔盒用户ID，用于标识奇穴配置的作者。<br>
                可在魔盒个人中心查看。
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <Badge 
        v-if="uid && !isValidUid"
        variant="destructive"
        class="text-xs"
      >
        格式错误
      </Badge>
    </div>

    <!-- 输入框 -->
    <div class="relative">
      <User class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        v-model="uid"
        type="text"
        placeholder="请输入您的魔盒UID（选填）"
        class="pl-10"
        :class="uid && !isValidUid ? 'border-destructive focus-visible:ring-destructive' : ''"
      />
    </div>

    <!-- 提示信息 -->
    <div class="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/30 p-3 rounded-md">
      <HelpCircle class="w-4 h-4 shrink-0 mt-0.5" />
      <div class="space-y-1">
        <p>如何获取魔盒UID：</p>
        <ol class="list-decimal list-inside space-y-0.5 ml-1">
          <li>登录 <a href="https://www.jx3box.com" target="_blank" class="text-primary hover:underline">JX3BOX魔盒</a></li>
          <li>进入个人中心</li>
          <li>复制页面中的UID数字</li>
        </ol>
      </div>
    </div>
  </div>
</template>
